import { useSyncExternalStore } from 'react'

/**
 * 라이트/다크 테마. 선택값은 'light' | 'dark' | 'system' 이고 <html data-theme> 에 실제 적용 테마를 단다.
 * 첫 페인트 전 적용은 index.html 의 인라인 스크립트가 같은 키로 한다 (깜빡임 방지) — 키·규칙을 바꾸면 거기도 같이.
 * 예전 뷰어 화면 전용 토글('viewerTheme')을 쓰던 계정은 그 값을 이어받는다.
 */
export type ThemePref = 'light' | 'dark' | 'system'

const KEY = 'theme'
const LEGACY_VIEWER_KEY = 'viewerTheme'
const media = window.matchMedia('(prefers-color-scheme: dark)')
const listeners = new Set<() => void>()

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY) ?? localStorage.getItem(LEGACY_VIEWER_KEY)
    return v === 'light' || v === 'dark' ? v : 'system'
  } catch {
    return 'system'
  }
}

let pref: ThemePref = readPref()

const resolve = (p: ThemePref) => (p === 'system' ? (media.matches ? 'dark' : 'light') : p)

function apply(animate: boolean) {
  const root = document.documentElement
  if (animate) {
    root.classList.add('theme-switching')
    window.setTimeout(() => root.classList.remove('theme-switching'), 250)
  }
  root.dataset.theme = resolve(pref)
  listeners.forEach(l => l())
}

media.addEventListener('change', () => { if (pref === 'system') apply(true) })
apply(false)

export function setThemePref(next: ThemePref) {
  pref = next
  try {
    if (next === 'system') localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, next)
    localStorage.removeItem(LEGACY_VIEWER_KEY)
  } catch { /* 저장 불가(사생활 보호 모드 등) — 이번 세션에만 적용 */ }
  apply(true)
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l) } }

export function useTheme() {
  const current = useSyncExternalStore(subscribe, () => pref)
  const resolved = useSyncExternalStore(subscribe, () => resolve(pref))
  return { pref: current, resolved, setPref: setThemePref }
}
